import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, IMAGES } from "@/constants";
import ROUTES from "@/routesData";
import FreightCalculator from "@/components/FreightCalculator";
import { useSiteSettings, waLink } from "@/siteSettings";

const MAPS_EMBED = "https://maps.google.com/maps?q=Jalan%20Gatot%20Subroto%20Baru%2C%20Makassar%2C%20Sulawesi%20Selatan&t=&z=15&ie=UTF8&iwloc=&output=embed";

const Header = () => {
  const settings = useSiteSettings();
  return (
  <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/80" data-testid="site-header">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
      <Link to="/" className="flex items-center space-x-3" data-testid="header-brand">
        <img src="/logo-mark.png" alt="Logo Nurrahma Logistics" className="h-10 w-auto" data-testid="header-logo" />
        <div>
          <span className="text-lg font-black tracking-tight text-blue-950 block leading-tight" data-testid="brand-name">NURRAHMA LOGISTICS</span>
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">{settings.tagline}</span>
        </div>
      </Link>
      <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-600">
        <a href="#layanan" className="hover:text-blue-900 transition-colors" data-testid="nav-layanan">Moda Layanan</a>
        <a href="#rute" className="hover:text-blue-900 transition-colors" data-testid="nav-rute">Jangkauan Rute</a>
        <a href="#armada" className="hover:text-blue-900 transition-colors" data-testid="nav-armada">Galeri Operasional</a>
        <a href="#alur" className="hover:text-blue-900 transition-colors" data-testid="nav-alur">Alur Kirim</a>
        <a href="#kontak" className="hover:text-blue-900 transition-colors" data-testid="nav-kontak">Hubungi Kami</a>
      </nav>
      <div className="flex items-center space-x-3">
        <a href="#kontak" className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-900 text-amber-300 font-semibold text-sm hover:bg-blue-950 transition-colors shadow-sm shadow-blue-950/10" data-testid="nav-cta-penawaran">
          Minta Penawaran
        </a>
      </div>
    </div>
  </header>
  );
};

const Hero = () => {
  const settings = useSiteSettings();
  return (
  <section className="relative min-h-[580px] lg:min-h-[640px] flex items-center justify-center bg-slate-900 text-white overflow-hidden py-24" data-testid="hero-section">
    <div className="absolute inset-0 z-0">
      <img src={IMAGES.hero} alt="Kapal kontainer bersandar di pelabuhan dengan crane dan truk kargo" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-slate-900/85 to-slate-950/90"></div>
      <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent"></div>
    </div>

    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal-entry">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white max-w-3xl mx-auto leading-tight" data-testid="hero-headline">
        {settings.heroTitle}
      </h1>
      <p className="mt-6 text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto font-normal leading-relaxed" data-testid="hero-subheadline">
        {settings.heroSubtitle}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        <a href="#kontak" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-amber-400 text-slate-950 font-bold text-base hover:bg-amber-300 transition-all transform hover:-translate-y-0.5 shadow-lg shadow-amber-400/20 text-center" data-testid="hero-cta-konsultasi">
          Konsultasi Tarif & Jadwal
        </a>
        <a href={waLink(settings)} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/10 text-white border border-white/25 font-bold text-base hover:bg-white/20 transition-colors text-center" data-testid="hero-cta-whatsapp">
          Chat WhatsApp
        </a>
      </div>

      <div className="mt-12 inline-flex items-center gap-6 text-xs text-slate-300 border border-slate-700/80 bg-slate-900/60 backdrop-blur-md px-6 py-3 rounded-full" data-testid="hero-location-pill">
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
          Titik Operasi: Jl. Gatot Subroto Baru No 17
        </span>
        <span className="hidden sm:inline text-slate-500">|</span>
        <span className="hidden sm:inline">Darat • Laut • Udara</span>
      </div>
    </div>
  </section>
  );
};

const Marquee = () => {
  const items = [
    "Pengiriman Port-to-Port & Door-to-Door",
    "Rute Prioritas Makassar, Parepare, Palopo, Bone",
    "Koneksi Kargo Laut & Udara ke Sorong, Jayapura, Timika, Merauke",
    "Konsolidasi Muatan UMKM & Proyek Perusahaan",
  ];
  const row = [...items, ...items];
  return (
    <div className="bg-blue-950 text-slate-300 py-3.5 border-y border-blue-900/60 overflow-hidden" data-testid="marquee-banner">
      <div className="animate-track whitespace-nowrap text-xs font-semibold uppercase tracking-widest text-slate-300">
        {row.map((item, i) => (
          <span key={i} className="mx-6">★ {item}</span>
        ))}
      </div>
    </div>
  );
};

const SERVICES = [
  {
    title: "Ekspedisi Darat Reguler & Carter",
    desc: "Distribusi lintas kabupaten di seluruh Sulawesi Selatan. Mendukung pengiriman retail, LTL (Less than Truckload), dan FTL (Full Truckload) armada box/bak.",
    points: [
      "Makassar ke Parepare, Palopo, Toraja, Bone, Bulukumba",
      "Penjemputan barang untuk pergudangan & UMKM",
      "Jadwal keberangkatan armada harian & berkala",
    ],
    cta: "Cek Jadwal Darat →",
    featured: false,
    testid: "service-darat",
  },
  {
    badge: "Spesialis Volume Besar",
    title: "Kargo Laut FCL & LCL Papua",
    desc: "Pengiriman kontainer dan curah dari Pelabuhan Soekarno-Hatta Makassar menuju pelabuhan utama di seluruh tanah Papua dengan tarif hemat.",
    points: [
      "Rute rutin ke Jayapura, Sorong, Manokwari, Timika, Merauke",
      "Layanan stuffing, stripping, dan handling pelabuhan",
      "Sangat cocok untuk pasokan logistik proyek & distributor",
    ],
    cta: "Hitung Kubikasi Kargo Laut →",
    featured: true,
    testid: "service-laut",
  },
  {
    title: "Kargo Udara Kilat & Prioritas",
    desc: "Solusi untuk komoditas bernilai tinggi, dokumen penting, sparepart darurat, atau bahan makanan yang membutuhkan estimasi waktu tercepat.",
    points: [
      "Pemberangkatan via Bandara Internasional Sultan Hasanuddin",
      "Port-to-Port & Door-to-Door antarpulau",
      "Prioritas handling untuk muatan sensitif waktu",
    ],
    cta: "Pesan Kargo Udara →",
    featured: false,
    testid: "service-udara",
  },
];

const PromoBar = () => {
  const settings = useSiteSettings();
  if (!settings.promo?.enabled || !settings.promo?.text) return null;
  return (
    <div className="bg-amber-400 text-slate-950 py-2.5 px-4 text-center text-xs sm:text-sm font-bold" data-testid="promo-banner">
      {settings.promo.text}{" "}
      <a href="#kontak" className="underline hover:no-underline" data-testid="promo-cta">{settings.promo.ctaText || "Minta Penawaran"} →</a>
    </div>
  );
};

const Services = () => {
  const settings = useSiteSettings();
  const services = Array.isArray(settings.services) && settings.services.length === 3 ? settings.services : SERVICES;
  return (
  <section id="layanan" className="py-24 bg-white border-b border-slate-200" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Portofolio Layanan</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight">Satu Pintu Tiga Moda Pengiriman</h2>
        <p className="text-slate-600 mt-4 leading-relaxed">Kelola semua kebutuhan logistik regional Anda tanpa perlu berganti vendor. Kami mengintegrasikan jalur darat antarkota, laut antarpulau, dan kargo udara kilat.</p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
        {services.map((s, idx) => {
          const featured = idx === 1;
          return (
          <div
            key={s.title}
            data-testid={s.testid || `service-card-${idx}`}
            className={
              featured
                ? "rounded-2xl border-2 border-blue-900/20 p-8 bg-blue-900 text-white flex flex-col justify-between relative shadow-lg shadow-blue-950/10"
                : "rounded-2xl border border-slate-200 p-8 bg-[#FAFCFF] flex flex-col justify-between hover:border-blue-300 transition-colors shadow-sm"
            }
          >
            <div>
              {featured && (
                <div className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold bg-amber-400 text-blue-950 uppercase tracking-wider mb-2">Spesialis Volume Besar</div>
              )}
              <h3 className={`text-xl font-bold ${featured ? "text-white" : "text-slate-900"}`}>{s.title}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-blue-100" : "text-slate-600"}`}>{s.desc}</p>
              <ul className={`mt-6 space-y-2.5 text-xs font-medium ${featured ? "text-blue-200" : "text-slate-700"}`}>
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${featured ? "bg-amber-400" : "bg-amber-500"}`}></span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`mt-8 pt-6 border-t ${featured ? "border-blue-800" : "border-slate-100"}`}>
              <a href="#kontak" className={`text-xs font-bold inline-flex items-center gap-1 ${featured ? "text-amber-300 hover:text-white" : "text-blue-900 hover:text-amber-600"} transition-colors`} data-testid={`service-card-${idx}-cta`}>
                {s.cta}
              </a>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

const RoutesSection = () => (
  <section id="rute" className="py-24 bg-[#EEF2F7] border-b border-slate-200" data-testid="routes-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-widest text-blue-900">Cakupan Wilayah</span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-2 tracking-tight">Konektivitas Sulsel Menuju Timur Nusantara</h2>
          <p className="text-slate-600 mt-4 leading-relaxed" data-testid="service-coverage">
            Beroperasi dari pusat logistik di Makassar (Jalan Gatot Subroto Baru No 17), kami menghubungkan rantai pasok ke seluruh 24 kabupaten/kota Sulawesi Selatan dan kota-kota strategis di Papua.
          </p>

          <div className="mt-8 space-y-4">
            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="coverage-sulsel">
              <h4 className="text-sm font-bold text-slate-900">Hub Sulawesi Selatan (Darat & Laut Lokal)</h4>
              <p className="text-xs text-slate-600 mt-1">Makassar, Maros, Pangkep, Barru, Parepare, Pinrang, Sidrap, Enrekang, Tana Toraja, Toraja Utara, Luwu, Palopo, Luwu Utara, Luwu Timur, Wajo, Soppeng, Bone, Sinjai, Bulukumba, Bantaeng, Jeneponto, Takalar, Gowa, Selayar.</p>
            </div>
            <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid="coverage-papua">
              <h4 className="text-sm font-bold text-slate-900">Koridor Kargo Papua (Laut & Udara)</h4>
              <p className="text-xs text-slate-600 mt-1">Jayapura, Sorong, Manokwari, Timika, Merauke, Biak, Nabire, Fakfak, Kaimana, Wamena (via transit udara).</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-slate-300 bg-gradient-to-br from-slate-800 via-blue-950 to-slate-900 p-8 text-white relative overflow-hidden shadow-xl" data-testid="hub-map-card">
            <div className="flex items-center justify-between pb-6 border-b border-slate-700/80">
              <div>
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Simpul Utama Logistik</span>
                <h3 className="text-lg font-bold text-white mt-0.5">Makassar Gateway Hub</h3>
              </div>
              <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Aktif Beroperasi</span>
            </div>

            <div className="py-8 grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700">
                <span className="text-[11px] font-mono text-slate-400 block">ASAL</span>
                <span className="text-sm font-bold text-white mt-1 block">Makassar</span>
                <span className="text-[10px] text-amber-300 mt-1 block font-medium">Jl. Gatot Subroto Baru</span>
              </div>
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs font-mono text-amber-400 font-bold">◄ 3 MODA ►</span>
                <div className="w-full h-0.5 bg-gradient-to-r from-blue-500 via-amber-400 to-emerald-400 my-2"></div>
                <span className="text-[10px] text-slate-300">Darat • Laut • Udara</span>
              </div>
              <div className="p-4 rounded-xl bg-slate-800/80 border border-slate-700">
                <span className="text-[11px] font-mono text-slate-400 block">TUJUAN</span>
                <span className="text-sm font-bold text-white mt-1 block">Sulsel & Papua</span>
                <span className="text-[10px] text-emerald-300 mt-1 block font-medium">24 Kab + 6 Provinsi</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-700/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-300 gap-3">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                Kantor: Jl. Gatot Subroto Baru No 17
              </span>
              <a href="#kontak" className="text-amber-400 font-bold hover:underline" data-testid="hub-cta-rute">Tanyakan Ketersediaan Rute →</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PopularRoutes = () => (
  <section className="py-16 bg-white border-b border-slate-200" data-testid="popular-routes-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Rute Populer</span>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">Ekspedisi dari Makassar ke Kota Tujuan Anda</h2>
        <p className="text-slate-600 mt-3 text-sm leading-relaxed">Pilih rute untuk melihat detail layanan, moda yang tersedia, dan cara cek ongkir.</p>
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {ROUTES.map((r) => (
          <Link key={r.slug} to={`/rute/${r.slug}`} className="p-5 rounded-xl bg-[#FAFCFF] border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group" data-testid={`popular-route-${r.slug}`}>
            <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">{r.moda}</span>
            <h3 className="text-sm font-bold text-slate-900 mt-1.5 group-hover:text-blue-900 transition-colors">{r.title}</h3>
            <span className="text-xs font-bold text-blue-900 mt-3 inline-block">Lihat Detail →</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const GALLERY = [
  { img: "/armada-nrl.jpg", tag: "Gudang & Armada NRL", label: "Armada Resmi di Gudang Gatot Subroto" },
  { img: IMAGES.gallery2, tag: "Muatan Laut", label: "Handling Petikemas Pelabuhan" },
  { img: IMAGES.gallery3, tag: "Kargo Udara", label: "Paletisasi Udara Kilat" },
  { img: IMAGES.gallery4, tag: "Pusat Sortir", label: "Inspeksi & Pengepakan Aman" },
];

const Gallery = () => (
  <section id="armada" className="py-24 bg-white border-b border-slate-200" data-testid="gallery-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Aktivitas & Fasilitas</span>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-2 tracking-tight">Galeri Operasional Lapangan</h2>
        <p className="text-slate-600 mt-3 text-sm leading-relaxed">Pengawasan ketat pada setiap tahap pemuatan, transit pelabuhan, dan pengantaran akhir.</p>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {GALLERY.map((g, i) => (
          <div key={g.label} className="aspect-[4/3] rounded-xl overflow-hidden relative shadow-sm group" data-testid={`gallery-item-${i + 1}`}>
            <img src={g.img} alt={g.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">{g.tag}</span>
              <h3 className="text-sm font-bold text-white mt-1">{g.label}</h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { title: "1. Kirim Rincian Muatan", desc: "Berikan info berat, volume, jenis barang, dan tujuan melalui formulir atau WhatsApp.", dot: "bg-blue-900" },
  { title: "2. Estimasi Tarif & Moda", desc: "Tim kami memberikan opsi moda terbaik (darat/laut/udara) beserta kalkulasi harga paling kompetitif.", dot: "bg-blue-900" },
  { title: "3. Drop-off atau Penjemputan", desc: "Antar ke kantor Jl. Gatot Subroto Baru No 17 atau manfaatkan layanan pick-up kami.", dot: "bg-blue-900" },
  { title: "4. Dispatch & Serah Terima", desc: "Barang diberangkatkan sesuai jadwal reguler dan diantarkan aman ke tujuan akhir.", dot: "bg-amber-500" },
];

const Flow = () => (
  <section id="alur" className="py-24 bg-[#FAFCFF] border-b border-slate-200" data-testid="flow-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Proses Kerja</span>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight">Alur Praktis Kirim Barang Bersama Kami</h2>
        <p className="text-slate-600 mt-4 leading-relaxed">Dari konsultasi awal hingga serah terima di alamat penerima, proses dirancang transparan dan efisien.</p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
        {STEPS.map((s) => (
          <div key={s.title} className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm relative">
            <div className={`w-3 h-3 rounded-full ${s.dot} mb-4`}></div>
            <h3 className="text-base font-bold text-slate-900">{s.title}</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = () => (
  <section className="py-20 bg-white border-b border-slate-200" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Untuk Siapa Kami Bekerja</span>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">Melayani UMKM, Korporasi & Mitra Ekspedisi</h2>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-xl border border-slate-200 bg-slate-50 flex flex-col justify-between" data-testid="trust-umkm">
          <div>
            <span className="text-xs font-semibold text-blue-900 uppercase">UMKM & Toko</span>
            <p className="text-sm text-slate-700 mt-3">Distribusi barang dagangan rutin antarkota di Sulawesi Selatan dengan tarif yang masuk akal untuk usaha kecil dan menengah.</p>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500">Muatan retail, sembako, dan dagangan harian</div>
        </div>

        <div className="p-6 rounded-xl border border-slate-200 bg-slate-50 flex flex-col justify-between" data-testid="trust-corporate">
          <div>
            <span className="text-xs font-semibold text-blue-900 uppercase">Perusahaan & Proyek</span>
            <p className="text-sm text-slate-700 mt-3">Pasokan logistik kontainer dan curah untuk kebutuhan proyek serta distributor, termasuk koridor kargo ke Papua.</p>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500">FCL, LCL, material proyek, dan komoditas niaga</div>
        </div>

        <div className="p-6 rounded-xl border border-slate-200 bg-blue-50/50 flex flex-col justify-center text-center items-center" data-testid="trust-mitra">
          <span className="text-xs font-bold text-blue-950 uppercase tracking-wider">Mitra Ekspedisi & Cargo</span>
          <p className="text-xs text-slate-600 mt-2">Terima titipan muatan lanjutan (forwarding) ke Sulawesi Selatan dan Papua dari ekspedisi daerah lain.</p>
          <a href="#kontak" className="mt-4 px-4 py-2 rounded-lg bg-blue-900 text-amber-300 font-bold text-xs hover:bg-blue-950 transition-colors" data-testid="trust-cta-mitra">
            Jadi Mitra Pengiriman
          </a>
        </div>
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: "Apakah melayani pengiriman barang satuan atau hanya partai besar?", a: "Kami melayani pengiriman perorangan, UMKM dengan muatan kecil (retail/LCL), hingga pengiriman kontainer penuh (FTL/FCL) untuk kebutuhan korporasi." },
  { q: "Bagaimana perhitungan biaya pengiriman kargo laut dan udara?", a: "Biaya dihitung berdasarkan berat aktual (kg) atau berat volumetrik (kubikasi), tergantung nilai yang lebih tinggi sesuai standar ekspedisi." },
  { q: "Apakah tersedia fasilitas penjemputan barang di lokasi pengirim?", a: "Ya, untuk area Makassar dan sekitarnya kami menyediakan armada pick-up sesuai jadwal yang disepakati." },
];

const FAQ = () => {
  const settings = useSiteSettings();
  const faqs = Array.isArray(settings.faqs) && settings.faqs.length > 0 ? settings.faqs : FAQS;
  return (
  <section className="py-20 bg-[#F4F7FB] border-b border-slate-200" data-testid="faq-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-xl mx-auto">
        <span className="text-xs font-bold uppercase tracking-widest text-blue-900">Pertanyaan Umum</span>
        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mt-2 tracking-tight">Hal yang Sering Ditanyakan</h2>
      </div>

      <div className="mt-10 space-y-4">
        {faqs.map((f) => (
          <div key={f.q} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900">{f.q}</h3>
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const CalculatorSection = () => (
  <section id="kalkulator" className="py-24 bg-[#EEF2F7] border-b border-slate-200" data-testid="calculator-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-blue-900">Cek Ongkir</span>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-2 tracking-tight">Estimasi Ongkir Transparan dari Makassar</h2>
        <p className="text-slate-600 mt-3 text-sm leading-relaxed">Tarif handling darat resmi ke lebih dari 70 kota tujuan di Sulawesi — tanpa biaya tersembunyi.</p>
      </div>
      <FreightCalculator />
    </div>
  </section>
);

const ROUTE_OPTIONS = [
  "Darat (Sulsel & Sekitarnya)",
  "Laut (Kargo / Kontainer Papua & Antarpulau)",
  "Udara (Kargo Kilat)",
  "Rekomendasikan yang Paling Hemat",
];

const Contact = () => {
  const settings = useSiteSettings();
  const [form, setForm] = useState({ sender_name: "", whatsapp: "", destination: "", route: ROUTE_OPTIONS[0], cargo_details: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/shipping-requests`, form);
      toast.success("Permintaan tarif terkirim! Tim kami akan segera menghubungi Anda.");
      setForm({ sender_name: "", whatsapp: "", destination: "", route: ROUTE_OPTIONS[0], cargo_details: "" });
    } catch (err) {
      toast.error("Gagal mengirim permintaan. Periksa kembali isian Anda atau coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white transition-shadow";
  const labelCls = "block text-xs font-semibold text-slate-700 mb-1";

  return (
    <section id="kontak" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Hubungi Nurrahma Logistics</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight">Konsultasikan Pengiriman Anda</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Kantor kami berlokasi di Jalan Gatot Subroto Baru No 17. Tim operasional siap membantu rute, jadwal keberangkatan armada, dan penawaran harga terbaik.
            </p>

            <div className="mt-8 space-y-4 text-sm text-slate-700">
              <div className="flex items-start gap-3">
                <span className="font-bold text-blue-900 shrink-0">Alamat:</span>
                <span data-testid="office-address">{settings.address}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-blue-900 shrink-0">Moda:</span>
                <span>Darat (Truk/Box) • Laut (Kontainer/Kapal Cepat) • Udara (Kargo Udara)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold text-blue-900 shrink-0">Cakupan:</span>
                <span>Seluruh Sulawesi Selatan & Papua</span>
              </div>
            </div>

            <div className="mt-8 p-5 rounded-xl bg-emerald-50 border border-emerald-200">
              <h3 className="text-sm font-bold text-emerald-950">Respon Cepat via WhatsApp</h3>
              <p className="text-xs text-emerald-800 mt-1">Butuh info muatan cepat atau jadwal kapal terdekat? Hubungi petugas kami langsung.</p>
              <a href={waLink(settings)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition-colors shadow-sm" data-testid="whatsapp-cta-button">
                Hubungi via WhatsApp
              </a>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="office-map">
              <iframe
                title="Peta Kantor Nurrahma Logistics - Jalan Gatot Subroto Baru No 17"
                src={MAPS_EMBED}
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="p-8 rounded-2xl bg-[#F8FAFD] border border-slate-200 shadow-md">
              <h3 className="text-xl font-bold text-slate-900">Formulir Permintaan Tarif & Penjemputan</h3>
              <p className="text-xs text-slate-600 mt-1">Isi formulir berikut, tim kami akan merespons dengan rincian biaya resmi.</p>

              <form className="mt-6 space-y-4" onSubmit={handleSubmit} data-testid="shipping-request-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="sender_name" className={labelCls}>Nama Lengkap / Perusahaan</label>
                    <input id="sender_name" name="sender_name" type="text" required value={form.sender_name} onChange={handleChange} className={inputCls} placeholder="Contoh: PT Sumber Rejeki / Bapak Andi" data-testid="form-sender-name" />
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className={labelCls}>Nomor WhatsApp / Kontak</label>
                    <input id="whatsapp" name="whatsapp" type="tel" required value={form.whatsapp} onChange={handleChange} className={inputCls} placeholder="08xxxxxxxxxx" data-testid="form-whatsapp" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="destination" className={labelCls}>Kota Asal - Kota Tujuan</label>
                    <input id="destination" name="destination" type="text" required value={form.destination} onChange={handleChange} className={inputCls} placeholder="Contoh: Makassar - Jayapura" data-testid="form-destination" />
                  </div>
                  <div>
                    <label htmlFor="route" className={labelCls}>Pilihan Moda Pengiriman</label>
                    <select id="route" name="route" value={form.route} onChange={handleChange} className={inputCls} data-testid="form-route">
                      {ROUTE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="cargo_details" className={labelCls}>Estimasi Muatan & Jenis Barang</label>
                  <textarea id="cargo_details" name="cargo_details" rows="3" required value={form.cargo_details} onChange={handleChange} className={inputCls} placeholder="Jelaskan jenis barang (misal: sembako, material proyek, sparepart) serta perkiraan berat/koli..." data-testid="form-cargo-details"></textarea>
                </div>

                <button type="submit" disabled={loading} className="w-full py-3.5 rounded-xl bg-blue-900 text-amber-300 font-bold text-sm hover:bg-blue-950 transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed" data-testid="form-submit-button">
                  {loading ? "Mengirim..." : "Kirim Permintaan Tarif"}
                </button>
                <p className="text-[11px] text-center text-slate-500">Data Anda terlindungi dan hanya digunakan untuk keperluan kalkulasi pengiriman.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900 text-xs" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div>
          <div className="inline-block bg-white rounded-lg px-3 py-2 mb-3">
            <img src="/logo.png" alt="CV Nur Rahma Logistics" className="h-10 w-auto" data-testid="footer-logo" />
          </div>
          <p className="mt-1 text-slate-400">Jalan Gatot Subroto Baru No 17 • Spesialis Rute Sulawesi Selatan & Papua</p>
        </div>
        <div className="flex items-center space-x-6 text-slate-400">
          <a href="#layanan" className="hover:text-white transition-colors">Moda Layanan</a>
          <a href="#rute" className="hover:text-white transition-colors">Rute</a>
          <a href="#armada" className="hover:text-white transition-colors">Galeri</a>
          <Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog-link">Blog Tips</Link>
          <a href="#kontak" className="hover:text-white transition-colors">Kontak</a>
        </div>
      </div>
    </div>
  </footer>
);

export default function LandingPage() {
  return (
    <div className="font-sans antialiased bg-[#F4F7FB] text-slate-800 min-h-screen">
      <Header />
      <PromoBar />
      <Hero />
      <Marquee />
      <Services />
      <RoutesSection />
      <PopularRoutes />
      <Gallery />
      <Flow />
      <Trust />
      <FAQ />
      <CalculatorSection />
      <Contact />
      <Footer />
    </div>
  );
}
